import { OptionsObject, useSnackbar } from "notistack";
import * as React from "react";
import { useDeviceSize } from "..";

import { NotificationsState } from "./notificationState";
import { Snack } from "./snack";
import {
    NotificationCallback,
    NotificationLevel,
    NotificationMessage,
    NotificationOptions,
    NotificationType,
} from "./types";

const generateKey = (key?: string) =>
    !!key ? key : Math.round(Math.random() * 1e5).toString();
const AUTO_HIDE_DURATION = 3000;

export type NotificationsActionTypes =
    | {
          type: "NOTIFICATION_SHOWN";
          notification: NotificationMessage;
      }
    | { type: "NOTIFICATION_CLOSED"; key: string }
    | { type: "CLOSE_ALL_AREA_NOTIFICATIONS" }
    | { type: "CLOSE_AREA_NOTIFICATION"; areaName: string }
    | { type: "CLOSE_MODAL_NOTIFICATION"; title: string };

export const notificationActions = (
    dispatch: React.Dispatch<NotificationsActionTypes>,
    device: "desktop" | "mobile",
    enqueueSnackbar: (
        message: React.ReactNode,
        options?: OptionsObject
    ) => React.ReactText,
    closeSnackbar: (key?: string | number) => void
) => {
    const showNotification = (
        type: NotificationType,
        level: NotificationLevel,
        title: string,
        message: React.ReactNode,
        key: string,
        callback?: NotificationCallback,
        area?: string,
        canClose?: boolean,
        customIcon?: React.ReactElement<any>
    ): string => {
        if (canClose !== false) {
            canClose = true;
        }

        dispatch({
            type: "NOTIFICATION_SHOWN",
            notification: {
                callback,
                key,
                level,
                title,
                message,
                type,
                area,
                canClose,
                customIcon,
            },
        });
        return key;
    };

    const showSnack = (
        level: NotificationLevel,
        message: React.ReactNode,
        options?: NotificationOptions
    ): string => {
        options = options || {};
        if (options.autoClose !== false) {
            options.autoClose = true;
        }

        const key = generateKey(options.key);
        const callback = () => {
            closeSnackbar(key);

            dispatch({ type: "NOTIFICATION_CLOSED", key });
            if (options.callback) options.callback();
        };

        enqueueSnackbar(message, {
            key,
            autoHideDuration: options.autoClose
                ? options.autoCloseDuration || AUTO_HIDE_DURATION
                : 5000,
            anchorOrigin: {
                vertical: device === "desktop" ? "bottom" : "top",
                horizontal: "center",
            },
            ClickAwayListenerProps: {
                onClickAway: () => {
                    /* for some reason the material snackbar will call
                       onClose when the user clicks anywhere on screen
                       we have to do this to essentially ignore it.
                    */
                },
            },
            onClose: !!options.autoClose ? callback : undefined,
            content: key => (
                <Snack
                    message={message}
                    level={level}
                    autoHide={options.autoClose}
                    onClose={callback}
                    canClose={options.canClose}
                    customIcon={options.customIcon}
                />
            ),
        });
        return showNotification(
            "snack",
            level,
            "",
            message,
            key,
            options.callback,
            undefined,
            options.canClose,
            options.customIcon
        );
    };

    const showModal = (
        level: NotificationLevel,
        title: string,
        message: React.ReactNode,
        options?: NotificationOptions
    ): string => {
        options = options || {};
        const key = generateKey(options.key);
        let timerId;
        const callback = () => {
            if (options.autoClose) {
                clearTimeout(timerId);
            }
            dispatch({ type: "NOTIFICATION_CLOSED", key });
            if (options.callback) options.callback();
        };

        if (options.autoClose) {
            timerId = setTimeout(
                callback,
                options.autoCloseDuration || AUTO_HIDE_DURATION
            );
        }

        return showNotification(
            "modal",
            level,
            title,
            message,
            key,
            callback,
            undefined,
            options.canClose,
            options.customIcon
        );
    };

    const showArea = (
        level: NotificationLevel,
        message: React.ReactNode,
        areaName: string,
        options?: NotificationOptions
    ) => {
        options = options || {};

        const key = generateKey(options.key);

        const callback = () => {
            dispatch({ type: "NOTIFICATION_CLOSED", key });
        };

        if (options.autoClose) {
            setTimeout(
                callback,
                options.autoCloseDuration || AUTO_HIDE_DURATION
            );
        }
        dispatch({ type: "CLOSE_AREA_NOTIFICATION", areaName });
        return showNotification(
            "area",
            level,
            "",
            message,
            key,
            callback,
            areaName,
            options.canClose,
            options.customIcon
        );
    };

    const closeAreaNotification = (areaName: string) => {
        dispatch({ type: "CLOSE_AREA_NOTIFICATION", areaName });
    };

    const closeAllAreaNotifications = () => {
        dispatch({ type: "CLOSE_ALL_AREA_NOTIFICATIONS" });
    };

    const closeNotification = (key: string) => {
        dispatch({ type: "NOTIFICATION_CLOSED", key });
    };

    const closeModalNotification = (title: string) => {
        dispatch({ type: "CLOSE_MODAL_NOTIFICATION", title });
    };

    const actions = {
        closeNotification,
        showSnack,
        showModal,
        showArea,
        closeAreaNotification,
        closeAllAreaNotifications,
        closeModalNotification,
    };

    return actions;
};
